<template>
  <div class="col-12 relative grey-bg full-height flex flex-column">
    <template v-if="route.matched.length === 3 && !route.meta.breadcrumb">
      <CategoryHeader :title="route.meta.title" :icon="route.meta.icon" />
    </template>

    <div v-if="loading" class="relative full-height flex items-center justify-center pb5">
      <div class="center">
        <div class="list-loader-blue"></div>
      </div>
    </div>

    <router-view v-else class="p3" />
  </div>
</template>

<script>
import { onBeforeMount, ref, provide, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { add, endOfDay, format } from 'date-fns';

import { getScheduledReservations, getCustomersReservations } from '@/mixins/requests';
import { handleSubscription } from '@/mixins/amplify';
import { computeSession } from '@/mixins/utils';

import CategoryHeader from '@/components/common/CategoryHeader';

export default {
  components: { CategoryHeader },
  setup () {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const loading = computed(() => store.state.loadingContent);
    const workspace = computed(() => store.state.siteInfo.workspace);
    const siteInfo = computed(() => store.state.siteInfo);
    const reservations = computed(() => store.state.reservations);
    const contacts = computed(() => store.state.contacts);

    const participants = ref([]);

    const getUserHoranetReservedSessions = async () => {
      await getScheduledReservations({ workspace: workspace.value }).then(res => {
        participants.value = res;
      }).catch(err => {
        console.log('err: ', err);
        participants.value = [];
      }).finally(() => {
        store.commit('setReservations', participants.value);
        store.commit('setLoading', false);
      });
    };

    const componentInicialization = async () => {
      store.commit('setLoadingContent', false);
      store.commit('setLoading', true);
      store.commit('resetCalendarDays');

      if (!siteInfo.value.is_internal_session && ['horanet'].includes(siteInfo.value.horanetSource)) {
        return getUserHoranetReservedSessions();
      }

      if (siteInfo.value.is_internal_session || ['postgres'].includes(siteInfo.value.horanetSource)) {
        await handleSubscription();
      }

      const currentDate = new Date();

      const args = {
        contacts: contacts.value.map(c => (siteInfo.value.is_internal_session ? c.uuid : c.horanet_id)),
        startDate: parseFloat(format(currentDate, 't')),
        endDate: parseFloat(format(endOfDay(add(currentDate, { years: 1 })), 't'))
      };

      if (siteInfo.value.is_internal_session) {
        args.workspace = siteInfo.value.workspace;
      }

      if (['postgres'].includes(siteInfo.value.horanetSource)) {
        args.codeSiteComptage = parseInt(siteInfo.value.code_site_comptage);
      }

      const response = await getCustomersReservations(args);

      if (response.success) {
        const field = siteInfo.value.is_internal_session ? 'uuid' : 'horanet_id';

        participants.value = response.data.map(r => {
          const participantInfo = contacts.value.find(c => c[field] === r.uuid);
          return {
            uuid: participantInfo.uuid,
            firstName: participantInfo.firstname,
            lastName: participantInfo.lastname,
            sessions: r.sessions.map(s => computeSession(s, participantInfo, field))
          };
        });

        if (siteInfo.value.is_internal_session && participants.value.some(p => p.sessions.some(s => s.clubSessionReservation))) {
          /* Get all club sessions in one array */
          const sessionsFromClubMembers = participants.value.flatMap(participant => participant.sessions.filter(session => session.clubSessionReservation));

          participants.value = participants.value.map(participant => {
            /* filter club sessions from every participant and put them all on the the parent (club) */
            participant.sessions = participant.sessions.filter(session => !session.clubSessionReservation);
            if (participant.uuid === store.state.user.uuid) {
              sessionsFromClubMembers.forEach(sess => {
                if (!participant.sessions.find(s => s.uuid === sess.uuid)) {
                  participant.sessions.push(sess);
                }
              });
            }
            return participant;
          });
        }
      }

      store.commit('setReservations', participants.value);
      store.commit('setLoading', false);
    };

    onBeforeMount(async () => {
      await componentInicialization();
    });

    watch(() => store.state.clubSessionUpdated, async (newVal, oldVal) => {
      if (newVal && !oldVal) {
        await componentInicialization();
        store.commit('setClubSessionUpdated', false);
      }
    });

    watch(reservations, () => {
      participants.value = reservations.value;
    });

    provide('participants', participants);
    return {
      router,
      route,
      loading,
      store
    };
  }
};

</script>
